import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import "@/App.css";

import SmoothScroll from "@/components/SmoothScroll";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import StickyDiscoveryCTA from "@/components/StickyDiscoveryCTA";
import ProtectedRoute from "@/components/ProtectedRoute";
import AuthCallback from "@/components/AuthCallback";
import ConsentBanner from "@/components/ConsentBanner";
import CvDropModal from "@/components/CvDropModal";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/lib/auth";
import { CandidateProvider } from "@/lib/candidate";

import HomePage from "@/pages/HomePage";
import ServicesPage from "@/pages/ServicesPage";
import AboutPage from "@/pages/AboutPage";
import ContactPage from "@/pages/ContactPage";
import AdminPage from "@/pages/AdminPage";
import AdminLoginPage from "@/pages/AdminLoginPage";
import ClientLoginPage from "@/pages/ClientLoginPage";
import ClientDashboardPage from "@/pages/ClientDashboardPage";
import JobsPage from "@/pages/JobsPage";
import JobDetailPage from "@/pages/JobDetailPage";
import ApplyPage from "@/pages/ApplyPage";
import InsightsPage from "@/pages/InsightsPage";
import InsightDetailPage from "@/pages/InsightDetailPage";
import BrandPage from "@/pages/BrandPage";
import SegmentPage from "@/pages/SegmentPage";
import ServiceDetailPage from "@/pages/ServiceDetailPage";
import HowWeWorkPage from "@/pages/HowWeWorkPage";
import CaseStudiesPage from "@/pages/CaseStudiesPage";
import CaseStudyPage from "@/pages/CaseStudyPage";
import BookDiagnosticPage from "@/pages/BookDiagnosticPage";
import LegalPage from "@/pages/LegalPage";

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);
  return null;
};

const AppShell = () => {
  const [cvOpen, setCvOpen] = useState(false);
  const location = useLocation();
  // Google OAuth returns with #session_id=... — process it synchronously
  // during render (BEFORE normal routes / ProtectedRoute) to avoid races.
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <>
      <ScrollToTop />
      <Header onOpenCv={() => setCvOpen(true)} />
      <main>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/services/:slug" element={<ServiceDetailPage />} />
          <Route path="/gcc-support" element={<SegmentPage slug="gcc-support" />} />
          <Route path="/ai-native-it-services" element={<SegmentPage slug="ai-native-it-services" />} />
          <Route path="/how-we-work" element={<HowWeWorkPage />} />
          <Route path="/case-studies" element={<CaseStudiesPage />} />
          <Route path="/case-studies/:slug" element={<CaseStudyPage />} />
          <Route path="/book-diagnostic" element={<BookDiagnosticPage />} />
          <Route path="/privacy" element={<LegalPage doc="privacy" />} />
          <Route path="/terms" element={<LegalPage doc="terms" />} />
          <Route path="/cookies" element={<LegalPage doc="cookies" />} />
          <Route path="/jobs" element={<JobsPage />} />
          <Route path="/jobs/:id" element={<JobDetailPage />} />
          <Route path="/apply" element={<ApplyPage />} />
          <Route path="/insights" element={<InsightsPage />} />
          <Route path="/insights/:slug" element={<InsightDetailPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/admin/login" element={<AdminLoginPage />} />
          <Route
            path="/admin"
            element={
              <ProtectedRoute role="admin">
                <AdminPage />
              </ProtectedRoute>
            }
          />
          <Route path="/client/login" element={<ClientLoginPage />} />
          <Route
            path="/client"
            element={
              <ProtectedRoute role="client">
                <ClientDashboardPage />
              </ProtectedRoute>
            }
          />
          <Route path="/brand" element={<BrandPage />} />
          <Route path="*" element={<HomePage />} />
        </Routes>
      </main>
      <Footer />
      <StickyDiscoveryCTA />
      <ConsentBanner />
      <CvDropModal open={cvOpen} onClose={() => setCvOpen(false)} />
    </>
  );
};

function App() {
  return (
    <div className="App grain">
      <HelmetProvider>
        <BrowserRouter>
          <AuthProvider>
            <CandidateProvider>
              <SmoothScroll>
                <AppShell />
              </SmoothScroll>
            </CandidateProvider>
          </AuthProvider>
        </BrowserRouter>
      </HelmetProvider>
      <Toaster
        position="bottom-right"
        theme="light"
        toastOptions={{
          style: {
            background: "#f5f1e8",
            border: "1px solid rgba(20,17,13,0.22)",
            color: "#14110d",
            borderRadius: 0,
          },
        }}
      />
    </div>
  );
}

export default App;
